import { useMemo, useState, useEffect, useRef } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Button,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BaseModal from '@/components/modals/BaseModal';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { close } from '@/store/uiSlice';
import { useI18nContext } from '@/i18n/i18n-react';
import { useGetYearSummaryQuery } from '@/store/apiSlice';

const YearSummaryModal = () => {
    const dispatch = useAppDispatch();
    const open = useAppSelector((state) => state.ui.open.yearSummary);
    const onClose = () => dispatch(close('yearSummary'));
    const { LL } = useI18nContext();

    const currentYear = new Date().getFullYear();
    const [year, setYear] = useState<number>(currentYear);

    useEffect(() => {
        setYear(currentYear);
    }, [currentYear]);

    type YearSummaryItem = {
        date: string;
        event?: string | null;
        eventId?: number | null;
        startTime?: string | null;
        endTime?: string | null;
        location?: string | null;
        excludedCategories?: number[] | null;
        weekday?: number | null;
        canceled?: number | null;
    };

    const { data: planData = [] } = useGetYearSummaryQuery(year, { skip: !open }) as {
        data?: YearSummaryItem[];
    };

    // Find next upcoming event in the selected year (including today)
    const nextUpcoming = useMemo(() => {
        const today0 = new Date();
        today0.setHours(0, 0, 0, 0);
        const future = planData
            .map((e: YearSummaryItem) => ({ ...e, d: new Date(e.date) }))
            .filter((e: any) => e.d >= today0)
            .sort((a: any, b: any) => a.d.getTime() - b.d.getTime());

        return future[0] ?? null;
    }, [planData]);

    const weekdays = [
        LL.weekdays.monday(),
        LL.weekdays.tuesday(),
        LL.weekdays.wednesday(),
        LL.weekdays.thursday(),
        LL.weekdays.friday(),
        LL.weekdays.saturday(),
        LL.weekdays.sunday(),
    ];

    // Helper: compute a canonical rows array once and derive text/html from it
    const buildClipboardRows = () => {
        return planData
            .slice()
            .sort(
                (a: YearSummaryItem, b: YearSummaryItem) =>
                    new Date(a.date).getTime() - new Date(b.date).getTime()
            )
            .map((ev: YearSummaryItem) => {
                const d = new Date(ev.date);
                const date = d.toLocaleDateString(undefined, {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                });
                const time = ev.startTime ? `${ev.startTime.slice(0, 5)}` : '';
                const weekday = weekdays[(d.getDay() + 6) % 7];
                const location = ev.location ?? '';
                const title = ev.event ?? LL.yearSummary.generatedTitle();
                const canceled = Boolean(ev.canceled);

                return {
                    date,
                    time,
                    weekday,
                    location,
                    title,
                    canceled,
                };
            });
    };

    const buildClipboardText = (): string => {
        const header = [
            LL.yearSummary.date(),
            LL.yearSummary.time(),
            LL.yearSummary.weekday(),
            LL.yearSummary.title(),
            LL.yearSummary.location(),
        ].join('\t');
        const rows = buildClipboardRows().map((r) =>
            // Keep column order consistent with table header: date, time, weekday, title, location
            [r.date, r.time, r.weekday, r.title, r.location].join('\t')
        );
        return [header, ...rows].join('\n');
    };

    const buildClipboardHTML = (): string => {
        const headerCellStyle = 'border:1px solid #ddd;font-weight:bold;background:#f5f5f5;';

        const headerHtml =
            `<tr>` +
            `<th style="${headerCellStyle}">${escapeHtml(LL.yearSummary.date())}</th>` +
            `<th style="${headerCellStyle}">${escapeHtml(LL.yearSummary.time())}</th>` +
            `<th style="${headerCellStyle}">${escapeHtml(LL.yearSummary.weekday())}</th>` +
            `<th style="${headerCellStyle}">${escapeHtml(LL.yearSummary.title())}</th>` +
            `<th style="${headerCellStyle}">${escapeHtml(LL.yearSummary.location())}</th>` +
            `</tr>`;

        const rowsHtml = buildClipboardRows()
            .map((r) => {
                const baseCellStyle = 'border:1px solid #ddd;';

                // Word often ignores text-decoration during paste. Semantic tags work better.
                const wrapIfCanceled = (text: string) => {
                    const escaped = escapeHtml(text);
                    if (!r.canceled) return escaped;
                    // Use multiple equivalent tags to maximize compatibility.
                    return `<del><s>${escaped}</s></del>`;
                };

                return (
                    `<tr>` +
                    `<td style="${baseCellStyle}">${wrapIfCanceled(r.date)}</td>` +
                    `<td style="${baseCellStyle}">${wrapIfCanceled(r.time)}</td>` +
                    `<td style="${baseCellStyle}">${wrapIfCanceled(r.weekday)}</td>` +
                    `<td style="${baseCellStyle}">${wrapIfCanceled(r.title)}</td>` +
                    `<td style="${baseCellStyle}">${wrapIfCanceled(r.location)}</td>` +
                    `</tr>`
                );
            })
            .join('');

        // Some Office apps behave better when HTML starts with a full document.
        return (
            `<!DOCTYPE html>` +
            `<html><head><meta charset="utf-8"></head><body>` +
            `<table style="border-collapse:collapse;">${headerHtml}${rowsHtml}</table>` +
            `</body></html>`
        );
    };

    // Minimal HTML escape helper
    const escapeHtml = (s: string) =>
        String(s)
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

    // Copy feedback state
    const [copied, setCopied] = useState(false);
    const [copyError, setCopyError] = useState(false);
    const copyTimeoutRef = useRef<number | null>(null);

    const copyToClipboard = async () => {
        const text = buildClipboardText();
        const html = buildClipboardHTML();

        try {
            // Try modern clipboard with HTML + plain text
            if (navigator.clipboard && (window as any).ClipboardItem) {
                const blobPlain = new Blob([text], { type: 'text/plain' });
                const blobHtml = new Blob([html], { type: 'text/html' });
                const item = new (window as any).ClipboardItem({
                    'text/plain': blobPlain,
                    'text/html': blobHtml,
                });
                // @ts-ignore
                await navigator.clipboard.write([item]);
            } else if (navigator.clipboard && navigator.clipboard.writeText) {
                // Fallback: only plain text
                await navigator.clipboard.writeText(text);
            } else {
                // Legacy fallback using textarea + execCommand
                const ta = document.createElement('textarea');
                ta.value = text;
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
            }

            // Visual feedback instead of alert: switch button to success + 'Copied'
            setCopied(true);
            setCopyError(false);
            if (copyTimeoutRef.current) {
                window.clearTimeout(copyTimeoutRef.current);
            }
            copyTimeoutRef.current = window.setTimeout(() => {
                setCopied(false);
                copyTimeoutRef.current = null;
            }, 3000);
        } catch (err) {
            // Visual error feedback: switch button to error state briefly
            setCopyError(true);
            setCopied(false);
            if (copyTimeoutRef.current) {
                window.clearTimeout(copyTimeoutRef.current);
            }
            copyTimeoutRef.current = window.setTimeout(() => {
                setCopyError(false);
                copyTimeoutRef.current = null;
            }, 3000);
        }
    };

    return (
        <BaseModal
            open={open}
            onClose={onClose}
            title={LL.yearSummary.title()}
            maxWidth="lg"
            actions={
                <>
                    <FormControl>
                        <InputLabel id="year-select-label-footer">
                            {LL.yearSummary.selectYearLabel()}
                        </InputLabel>
                        <Select
                            labelId="year-select-label-footer"
                            size="small"
                            value={year}
                            label={LL.yearSummary.selectYearLabel()}
                            onChange={(e) => setYear(Number(e.target.value))}
                            sx={{ minWidth: 160 }}
                        >
                            {(() =>
                                Array.from({ length: 5 }, (_, i) => currentYear - 3 + i).map(
                                    (y) => (
                                        <MenuItem value={y} key={y}>
                                            {y}
                                        </MenuItem>
                                    )
                                ))()}
                        </Select>
                    </FormControl>

                    <Box sx={{ flex: 1 }} />

                    <Button
                        variant={copied ? 'contained' : 'outlined'}
                        color={copied ? 'success' : copyError ? 'error' : 'primary'}
                        onClick={copyToClipboard}
                        startIcon={<ContentCopyIcon />}
                    >
                        {copied ? LL.yearSummary.copied() : LL.yearSummary.copyButton()}
                    </Button>
                    <Button variant="contained" onClick={onClose}>
                        {LL.yearSummary.closeButton()}
                    </Button>
                </>
            }
        >
            <TableContainer
                component={Paper}
                variant="outlined"
                sx={{ m: '16px', width: 'calc(100% - 32px)', overflowX: 'hidden' }}
            >
                <Table sx={{ tableLayout: 'fixed', width: '100%' }}>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>
                                {LL.yearSummary.date()}
                            </TableCell>
                            <TableCell sx={{ minWidth: 40, whiteSpace: 'nowrap' }}>
                                {LL.yearSummary.time()}
                            </TableCell>
                            <TableCell sx={{ minWidth: 120, whiteSpace: 'nowrap' }}>
                                {LL.yearSummary.weekday()}
                            </TableCell>
                            <TableCell sx={{ minWidth: 200 }}>{LL.yearSummary.title()}</TableCell>
                            <TableCell sx={{ minWidth: 160 }}>
                                {LL.yearSummary.location()}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {planData.map((ev: YearSummaryItem) => {
                            const d = new Date(ev.date);
                            const dateStr = d.toLocaleDateString(undefined, {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                            });
                            const timeStr = ev.startTime
                                ? ev.startTime.slice(0, 5)
                                : (ev.startTime ?? '');
                            const weekday = weekdays[(d.getDay() + 6) % 7];
                            const title = ev.event ?? LL.yearSummary.generatedTitle();
                            const isNext = nextUpcoming?.date === ev.date;
                            const isCanceled = Boolean(ev.canceled);

                            return (
                                <TableRow
                                    key={ev.date}
                                    sx={{
                                        ...(isNext ? { bgcolor: 'rgba(25, 118, 210, 0.08)' } : {}),
                                    }}
                                >
                                    <TableCell
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            textDecoration: isCanceled ? 'line-through' : 'none',
                                            color: isCanceled ? 'text.secondary' : 'inherit',
                                        }}
                                    >
                                        {dateStr}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            textDecoration: isCanceled ? 'line-through' : 'none',
                                            color: isCanceled ? 'text.secondary' : 'inherit',
                                        }}
                                    >
                                        {timeStr ? ` ${timeStr}` : ''}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            whiteSpace: 'nowrap',
                                            textDecoration: isCanceled ? 'line-through' : 'none',
                                            color: isCanceled ? 'text.secondary' : 'inherit',
                                        }}
                                    >
                                        {weekday}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                            textDecoration: isCanceled ? 'line-through' : 'none',
                                            color: isCanceled ? 'text.secondary' : 'inherit',
                                        }}
                                    >
                                        {title}
                                    </TableCell>
                                    <TableCell
                                        sx={{
                                            wordBreak: 'break-word',
                                            overflowWrap: 'anywhere',
                                            whiteSpace: 'nowrap',
                                            textDecoration: isCanceled ? 'line-through' : 'none',
                                            color: isCanceled ? 'text.secondary' : 'inherit',
                                        }}
                                    >
                                        {ev.location ?? ''}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </TableContainer>
        </BaseModal>
    );
};

export default YearSummaryModal;
